import { useState, useEffect, useCallback, useRef } from 'react';
import { CouponData, CouponProvider } from '../types/campaign';
import { getDefaultCoupons } from '../services/couponService';
import logger from '../utils/logger';
/**
 * Entry in the coupon map
 */
export interface CouponMapEntry {
    coupon: CouponData;
    source: 'user' | 'default'; // Track origin of the coupon
}

/**
 * Map key format: `${accountType}:${userPlanType}:${paymentCycle}`
 * Example: "personal:plus:monthly" or "business:pro:annually"
 */
type CouponMap = Map<string, CouponMapEntry>;

export interface UseCouponMapResult {
    couponMap: CouponMap;
    isLoading: boolean;
    error: string | null;
    getCouponForSlot: (
        accountType: string,
        userPlanType: string,
        paymentCycle: string
    ) => CouponMapEntry | null;
    setUserCoupon: (coupon: CouponData) => void;
    refreshDefaults: () => Promise<void>;
}

/**
 * Convert plan type terminology to user/coupon account_type terminology
 * Plans use "personal"/"business", users/coupons use "private"/"business"
 */
function planTypeToAccountType(planType: string): string {
    return planType === 'personal' ? 'private' : planType;
}

/**
 * Generate a map key from account type, plan type, and payment cycle
 */
function getMapKey(
    accountType: string,
    userPlanType: string,
    paymentCycle: string
): string {
    return `${accountType}:${userPlanType}:${paymentCycle}`;
}

/**
 * Custom hook to manage the coupon map for gap-filling
 *
 * The coupon map stores coupons indexed by account_type:user_plan_type:payment_cycle
 * When the user toggles billing cycle, we look up the appropriate coupon from this map
 *
 * @param provider - The payment provider (apple or charge_bee)
 * @param initialUserCoupon - The user's initial coupon (from URL param or manual entry)
 */
export function useCouponMap(
    provider: CouponProvider,
    initialUserCoupon: CouponData | null
): UseCouponMapResult {
    const [couponMap, setCouponMap] = useState<CouponMap>(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Track if we've already initialized to prevent duplicate fetches
    const initializedRef = useRef(false);
    // Track the provider to detect changes
    const providerRef = useRef(provider);

    /**
     * Build the coupon map from default coupons and user's coupon
     */
    const buildMap = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const map = new Map<string, CouponMapEntry>();

            // 1. Fetch and add default coupons
            logger.debug(
                '[COUPON-MAP] Fetching default coupons for provider:',
                provider
            );
            const defaults = await getDefaultCoupons(provider);
            logger.debug(
                '[COUPON-MAP] Received default coupons:',
                defaults.length
            );

            for (const coupon of defaults) {
                // Use user_plan_type for matching (e.g., 'plus', 'pro', 'basic')
                const accountType = coupon.account_type;
                const userPlanType = coupon.user_plan_type;
                const paymentCycle = coupon.payment_cycle;

                if (accountType && userPlanType && paymentCycle) {
                    const key = getMapKey(
                        accountType,
                        userPlanType,
                        paymentCycle
                    );
                    map.set(key, { coupon, source: 'default' });
                    logger.debug(
                        '[COUPON-MAP] Added default coupon:',
                        key,
                        coupon.coupon_code
                    );
                }
            }

            // 2. Override with user's coupon (if provided)
            if (initialUserCoupon) {
                const accountType = initialUserCoupon.account_type;
                const userPlanType = initialUserCoupon.user_plan_type;
                const paymentCycle = initialUserCoupon.payment_cycle;

                if (accountType && userPlanType && paymentCycle) {
                    const key = getMapKey(
                        accountType,
                        userPlanType,
                        paymentCycle
                    );
                    map.set(key, { coupon: initialUserCoupon, source: 'user' });
                    logger.debug(
                        '[COUPON-MAP] User coupon overrides slot:',
                        key,
                        initialUserCoupon.coupon_code
                    );
                } else {
                    logger.debug(
                        '[COUPON-MAP] User coupon missing matching fields:',
                        {
                            accountType,
                            userPlanType,
                            paymentCycle,
                        }
                    );
                }
            }

            setCouponMap(map);
            logger.debug('[COUPON-MAP] Map built with', map.size, 'entries');
        } catch (err) {
            console.error('[COUPON-MAP] Error building coupon map:', err);
            setError('Failed to load default coupons');
        } finally {
            setIsLoading(false);
        }
    }, [provider, initialUserCoupon]);

    // Initialize the map on mount and when provider/initialUserCoupon changes
    useEffect(() => {
        // Check if provider changed
        if (providerRef.current !== provider) {
            providerRef.current = provider;
            initializedRef.current = false;
        }

        if (!initializedRef.current) {
            initializedRef.current = true;
            buildMap();
        }
    }, [buildMap, provider]);

    // Update map when initialUserCoupon changes (but preserve existing defaults)
    useEffect(() => {
        if (initialUserCoupon && initializedRef.current) {
            const accountType = initialUserCoupon.account_type;
            const userPlanType = initialUserCoupon.user_plan_type;
            const paymentCycle = initialUserCoupon.payment_cycle;

            if (accountType && userPlanType && paymentCycle) {
                const key = getMapKey(accountType, userPlanType, paymentCycle);
                setCouponMap((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(key, {
                        coupon: initialUserCoupon,
                        source: 'user',
                    });
                    logger.debug('[COUPON-MAP] Updated user coupon slot:', key);
                    return newMap;
                });
            }
        }
    }, [initialUserCoupon]);

    /**
     * Get the coupon for a specific slot
     * @param accountType - Can be plan terminology ("personal"/"business") or coupon terminology ("private"/"business")
     */
    const getCouponForSlot = useCallback(
        (
            accountType: string,
            userPlanType: string,
            paymentCycle: string
        ): CouponMapEntry | null => {
            // Normalize plan type terminology to coupon account_type terminology
            // "personal" (plan) -> "private" (coupon/user)
            const normalizedAccountType = planTypeToAccountType(accountType);
            const key = getMapKey(
                normalizedAccountType,
                userPlanType,
                paymentCycle
            );
            const entry = couponMap.get(key) || null;
            logger.debug(
                '[COUPON-MAP] Looking up slot:',
                key,
                entry ? 'found' : 'not found'
            );
            return entry;
        },
        [couponMap]
    );

    /**
     * Set a user coupon (from manual entry)
     * This will override the slot for this coupon's account_type:user_plan_type:payment_cycle
     */
    const setUserCoupon = useCallback((coupon: CouponData) => {
        const accountType = coupon.account_type;
        const userPlanType = coupon.user_plan_type;
        const paymentCycle = coupon.payment_cycle;

        if (accountType && userPlanType && paymentCycle) {
            const key = getMapKey(accountType, userPlanType, paymentCycle);
            setCouponMap((prev) => {
                const newMap = new Map(prev);
                newMap.set(key, { coupon, source: 'user' });
                logger.debug(
                    '[COUPON-MAP] User set coupon for slot:',
                    key,
                    coupon.coupon_code
                );
                return newMap;
            });
        } else {
            console.warn(
                '[COUPON-MAP] Cannot set user coupon - missing matching fields:',
                {
                    accountType,
                    userPlanType,
                    paymentCycle,
                }
            );
        }
    }, []);

    /**
     * Refresh default coupons from the API
     */
    const refreshDefaults = useCallback(async () => {
        initializedRef.current = false;
        await buildMap();
    }, [buildMap]);

    return {
        couponMap,
        isLoading,
        error,
        getCouponForSlot,
        setUserCoupon,
        refreshDefaults,
    };
}

export default useCouponMap;
